'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrivateWikiSlot } from '@/lib/wiki/private-wiki-slot-types';
import { Loader2, MoreVertical, Trash2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BetterTooltip } from '@/components/ui/tooltip';
import { useDeletePrivateWiki, usePrivateWikiSlots } from '@/data/wiki/use-private-wiki-slots';
import { formatExactTimestamp } from '@/lib/utils/date-formatters';
function formatReplacementDate(value: string): string {
  return formatExactTimestamp(value).replace(' at 12:00 AM', '');
}
function getDeleteDescription(slot: PrivateWikiSlot): string {
  if (!slot.replaceableAt || new Date(slot.replaceableAt) <= new Date()) {
    return 'This permanently deletes the wiki from cubic and frees space for another private wiki.';
  }
  return `This permanently deletes the wiki from cubic. You can replace it with another private wiki on ${formatReplacementDate(slot.replaceableAt)}.`;
}
type DeleteWikiDialogProps = {
  installationId: number;
  slot: PrivateWikiSlot;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
function DeleteWikiDialog(t0) {
  const $ = _c(37);
  const {
    installationId,
    slot,
    open,
    onOpenChange
  } = t0;
  const router = useRouter();
  const deleteWiki = useDeletePrivateWiki();
  let t1;
  if ($[0] !== deleteWiki || $[1] !== installationId || $[2] !== onOpenChange || $[3] !== router || $[4] !== slot.latestWikiId || $[5] !== slot.repositoryId || $[6] !== slot.reservationId) {
    t1 = async () => {
      ;
      try {
        const result = await deleteWiki.mutateAsync({
          installationId,
          repositoryId: slot.repositoryId,
          reservationId: slot.reservationId,
          latestWikiId: slot.latestWikiId
        });
        onOpenChange(false);
        if (result.slotReleased) {
          toast.success("Wiki deleted. One private wiki slot is now available.");
        } else {
          if (result.replaceableAt) {
            toast.success(`Wiki deleted. Its slot becomes available on ${formatReplacementDate(result.replaceableAt)}.`);
          } else {
            toast.success("Wiki deleted.");
          }
        }
        router.push("/wiki");
      } catch (t2) {
        const error = t2;
        const message = error instanceof Error ? error.message : "Failed to delete wiki";
        toast.error(message);
      }
    };
    $[0] = deleteWiki;
    $[1] = installationId;
    $[2] = onOpenChange;
    $[3] = router;
    $[4] = slot.latestWikiId;
    $[5] = slot.repositoryId;
    $[6] = slot.reservationId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleDelete = t1;
  const t2 = deleteWiki.isPending ? undefined : onOpenChange;
  const t3 = `Delete ${slot.fullName} wiki?`;
  let t4;
  if ($[8] !== t3) {
    t4 = <AlertDialogTitle>{t3}</AlertDialogTitle>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== slot) {
    t5 = getDeleteDescription(slot);
    $[10] = slot;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <AlertDialogDescription>{t5}</AlertDialogDescription>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <AlertDialogHeader>{t4}{t6}</AlertDialogHeader>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== deleteWiki.isPending) {
    t8 = <AlertDialogCancel disabled={deleteWiki.isPending}>Cancel</AlertDialogCancel>;
    $[17] = deleteWiki.isPending;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== handleDelete) {
    t9 = event => {
      event.preventDefault();
      handleDelete();
    };
    $[19] = handleDelete;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== deleteWiki.isPending) {
    t10 = deleteWiki.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null;
    $[21] = deleteWiki.isPending;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== deleteWiki.isPending || $[24] !== t10 || $[25] !== t9) {
    t11 = <AlertDialogAction onClick={t9} disabled={deleteWiki.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t10}Delete wiki</AlertDialogAction>;
    $[23] = deleteWiki.isPending;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t8) {
    t12 = <AlertDialogFooter>{t8}{t11}</AlertDialogFooter>;
    $[27] = t11;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t7) {
    t13 = <AlertDialogContent>{t7}{t12}</AlertDialogContent>;
    $[30] = t12;
    $[31] = t7;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== open || $[34] !== t13 || $[35] !== t2) {
    t14 = <AlertDialog open={open} onOpenChange={t2}>{t13}</AlertDialog>;
    $[33] = open;
    $[34] = t13;
    $[35] = t2;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
function WikiDeleteMenuItemContent(t0) {
  const $ = _c(12);
  const {
    installationId,
    slot
  } = t0;
  const [open, setOpen] = useState(false);
  const t1 = !slot.canDelete || slot.inUse;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      event.preventDefault();
      setOpen(true);
    };
    t3 = <Trash2 className="h-3.5 w-3.5" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  const t4 = slot.inUse ? "Wiki in use" : "Delete wiki";
  let t5;
  if ($[2] !== t1 || $[3] !== t4) {
    t5 = <DropdownMenuItem className="text-red-400 focus:text-red-400 hover:bg-red-950/60 focus:bg-red-950/60" disabled={t1} onSelect={t2}>{t3}{t4}</DropdownMenuItem>;
    $[2] = t1;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== installationId || $[6] !== open || $[7] !== slot) {
    t6 = <DeleteWikiDialog installationId={installationId} slot={slot} open={open} onOpenChange={setOpen} />;
    $[5] = installationId;
    $[6] = open;
    $[7] = slot;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export function WikiDeleteMenuItem(t0) {
  const $ = _c(6);
  const {
    installationId,
    repositoryId
  } = t0;
  const {
    data
  } = usePrivateWikiSlots(installationId);
  let t1;
  if ($[0] !== data?.slots || $[1] !== repositoryId) {
    t1 = data?.slots.find(candidate => candidate.repositoryId === repositoryId && !candidate.deletedAt);
    $[0] = data?.slots;
    $[1] = repositoryId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const slot = t1;
  if (!installationId || !slot) {
    return null;
  }
  let t2;
  if ($[3] !== installationId || $[4] !== slot) {
    t2 = <WikiDeleteMenuItemContent installationId={installationId} slot={slot} />;
    $[3] = installationId;
    $[4] = slot;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function WikiDeleteMenu(t0) {
  const $ = _c(7);
  const {
    installationId,
    repositoryId
  } = t0;
  const {
    data
  } = usePrivateWikiSlots(installationId);
  let t1;
  if ($[0] !== data?.slots || $[1] !== repositoryId) {
    t1 = data?.slots.find(candidate => candidate.repositoryId === repositoryId && !candidate.deletedAt);
    $[0] = data?.slots;
    $[1] = repositoryId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const slot = t1;
  if (!installationId || !slot) {
    return null;
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <BetterTooltip content="More actions" side="bottom"><div><DropdownMenuTrigger asChild={true}><Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-white"><MoreVertical className="h-3 w-3" /><span className="sr-only">More actions</span></Button></DropdownMenuTrigger></div></BetterTooltip>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== installationId || $[5] !== slot) {
    t3 = <DropdownMenu>{t2}<DropdownMenuContent align="end" className="min-w-40"><WikiDeleteMenuItemContent installationId={installationId} slot={slot} /></DropdownMenuContent></DropdownMenu>;
    $[4] = installationId;
    $[5] = slot;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}